import React, { useCallback, useEffect, useState } from "react";
import { Clock3, Menu } from "lucide-react";
import "./utility.css";

const OPS = {
  "+": (a, b) => a + b,
  "−": (a, b) => a - b,
  "×": (a, b) => a * b,
  "÷": (a, b) => (b === 0 ? NaN : a / b),
};

const format = (n) => {
  if (!isFinite(n)) return "Cannot divide by zero";
  const rounded = Math.round(n * 1e12) / 1e12;
  return String(rounded);
};

export default function CalculatorApp() {
  const [display, setDisplay] = useState("0");
  const [expression, setExpression] = useState("");
  const [pending, setPending] = useState(null); // { value, op }
  const [fresh, setFresh] = useState(true);
  const [memory, setMemory] = useState(null);

  const digit = useCallback(
    (d) => {
      setDisplay((cur) => {
        if (fresh || cur === "0") return d === "." ? "0." : d;
        if (d === "." && cur.includes(".")) return cur;
        return cur + d;
      });
      setFresh(false);
    },
    [fresh]
  );

  const applyOp = useCallback(
    (op) => {
      const value = parseFloat(display);
      if (pending && !fresh) {
        const result = OPS[pending.op](pending.value, value);
        setDisplay(format(result));
        setPending({ value: result, op });
        setExpression(`${format(result)} ${op}`);
      } else {
        setPending({ value, op });
        setExpression(`${format(value)} ${op}`);
      }
      setFresh(true);
    },
    [display, fresh, pending]
  );

  const equals = useCallback(() => {
    if (!pending) return;
    const value = parseFloat(display);
    const result = OPS[pending.op](pending.value, value);
    setExpression(`${format(pending.value)} ${pending.op} ${format(value)} =`);
    setDisplay(format(result));
    setPending(null);
    setFresh(true);
  }, [display, pending]);

  const unary = (kind) => {
    const value = parseFloat(display);
    const map = {
      "1/x": 1 / value,
      "x²": value * value,
      "√x": Math.sqrt(value),
      "±": -value,
      "%": value / 100,
    };
    setDisplay(format(map[kind]));
    setFresh(true);
  };

  const clearAll = () => {
    setDisplay("0");
    setExpression("");
    setPending(null);
    setFresh(true);
  };

  const backspace = () =>
    setDisplay((cur) => (cur.length > 1 ? cur.slice(0, -1) : "0"));

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (/^[0-9]$/.test(k)) digit(k);
      else if (k === ".") digit(".");
      else if (k === "+") applyOp("+");
      else if (k === "-") applyOp("−");
      else if (k === "*") applyOp("×");
      else if (k === "/") applyOp("÷");
      else if (k === "Enter" || k === "=") equals();
      else if (k === "Backspace") backspace();
      else if (k === "Escape") clearAll();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [applyOp, digit, equals]);

  const Key = ({ label, onClick, variant }) => (
    <button className={`calc-key ${variant ? `calc-key--${variant}` : ""}`} onClick={onClick}>
      {label}
    </button>
  );

  return (
    <div className="calc">
      <div className="calc-head">
        <Menu size={16} />
        <span>Standard</span>
        <Clock3 size={16} className="calc-head__right" />
      </div>

      <div className="calc-display">
        <div className="calc-expression">{expression}</div>
        <div className="calc-value" aria-live="polite">
          {display}
        </div>
      </div>

      <div className="calc-memory">
        {["MC", "MR", "M+", "M−", "MS"].map((m) => (
          <button
            key={m}
            className="calc-mem"
            disabled={memory === null && (m === "MC" || m === "MR")}
            onClick={() => {
              const v = parseFloat(display);
              if (m === "MS") setMemory(v);
              else if (m === "MC") setMemory(null);
              else if (m === "MR" && memory !== null) {
                setDisplay(format(memory));
                setFresh(true);
              } else if (m === "M+") setMemory((mm) => (mm || 0) + v);
              else if (m === "M−") setMemory((mm) => (mm || 0) - v);
            }}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="calc-pad">
        <Key label="%" onClick={() => unary("%")} variant="fn" />
        <Key label="CE" onClick={() => setDisplay("0")} variant="fn" />
        <Key label="C" onClick={clearAll} variant="fn" />
        <Key label="⌫" onClick={backspace} variant="fn" />

        <Key label="1/x" onClick={() => unary("1/x")} variant="fn" />
        <Key label="x²" onClick={() => unary("x²")} variant="fn" />
        <Key label="√x" onClick={() => unary("√x")} variant="fn" />
        <Key label="÷" onClick={() => applyOp("÷")} variant="fn" />

        <Key label="7" onClick={() => digit("7")} />
        <Key label="8" onClick={() => digit("8")} />
        <Key label="9" onClick={() => digit("9")} />
        <Key label="×" onClick={() => applyOp("×")} variant="fn" />

        <Key label="4" onClick={() => digit("4")} />
        <Key label="5" onClick={() => digit("5")} />
        <Key label="6" onClick={() => digit("6")} />
        <Key label="−" onClick={() => applyOp("−")} variant="fn" />

        <Key label="1" onClick={() => digit("1")} />
        <Key label="2" onClick={() => digit("2")} />
        <Key label="3" onClick={() => digit("3")} />
        <Key label="+" onClick={() => applyOp("+")} variant="fn" />

        <Key label="±" onClick={() => unary("±")} />
        <Key label="0" onClick={() => digit("0")} />
        <Key label="." onClick={() => digit(".")} />
        <Key label="=" onClick={equals} variant="equals" />
      </div>
    </div>
  );
}
